/**
 * OP-3133: tells a share-link page whether this page load *landed* on it.
 *
 * A lesson's Pretty URL (`/standalone/<uniqueName>`, or `/<unit>/<lesson>` for a
 * unit lesson) is a route the app also navigates to from menus and dashboards, so
 * the route alone cannot say whether the user used the link. The first URL of the
 * page load can: it is recorded once, when this module is first evaluated at app
 * bootstrap, before any routing has happened.
 *
 * A logged-out visitor is sent to the login page with the link in `returnUrl`
 * (see AuthGate). Some sign-in flows reload the page before coming back, so when
 * the page load starts on a URL that carries `returnUrl`, that is the entry path.
 *
 * Privacy: only the *pathname* is kept, in memory, for comparison. The query
 * string is dropped and nothing here is ever sent to analytics; the only
 * analytics output derived from it is the constant `access_method` value (see
 * mixpanelAccess.ts).
 */

const RETURN_URL_PARAM = "returnUrl";

export type EntryLocation = { pathname: string; search: string };

export type DirectEntryTracker = {
  /** The pathname this page load started on (or was redirected to log in for). */
  getEntryPathname: () => string;
  /**
   * True while the given pathname is the one this page load started on and the
   * entry has not been consumed.
   */
  isDirectEntry: (pathname: string) => boolean;
  /**
   * Marks the entry as used, so a later in-app visit to the same route (back
   * button, menu link) is not a direct entry.
   */
  consumeDirectEntry: () => void;
};

const stripTrailingSlash = (pathname: string): string =>
  pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;

/** The path the user was trying to reach when the page load started. Never keeps a query string. */
export const resolveEntryPathname = ({ pathname, search }: EntryLocation): string => {
  // AuthGate writes `?returnUrl=<path>` unencoded, so a link's own query string
  // follows as `&...`; URLSearchParams then yields just the path, which is all
  // that is compared here.
  const returnUrl = new URLSearchParams(search).get(RETURN_URL_PARAM);
  if (returnUrl && returnUrl.startsWith("/")) {
    return stripTrailingSlash(returnUrl.split("?")[0]);
  }
  return stripTrailingSlash(pathname.split("?")[0]);
};

/** Builds a tracker for one page load. The app uses the default instance below. */
export const createDirectEntryTracker = (
  location: EntryLocation | undefined,
): DirectEntryTracker => {
  const entryPathname = location ? resolveEntryPathname(location) : "";
  let consumed = false;

  return {
    getEntryPathname: () => entryPathname,
    isDirectEntry: (pathname) =>
      !consumed &&
      entryPathname !== "" &&
      stripTrailingSlash(pathname) === entryPathname,
    consumeDirectEntry: () => {
      consumed = true;
    },
  };
};

const defaultTracker = createDirectEntryTracker(
  typeof window !== "undefined" ? window.location : undefined,
);

export const getEntryPathname = defaultTracker.getEntryPathname;
export const isDirectEntry = defaultTracker.isDirectEntry;
export const consumeDirectEntry = defaultTracker.consumeDirectEntry;
